import { TSentry } from './client';
import { EClaimPrivacy } from '../../components/profile/PublicStatusPopover';
import { PrivateScopesMode } from '../../requests/user';
import { ProviderType } from './provider';
import { IClientFull } from './clients';
import { emptySplitApi } from './api';
import { EEmailAction, endPoints, ETags } from 'src/enums';
import { createFetchArgs, createFetchArgsWithBody } from '../helpers';

export enum TargetType {
  user = 'USER',
  client = 'CLIENT',
  provider = 'PROVIDER',
}

export interface IRuleValidationData {
  active: boolean;
  title: string;
  error: string;
  regex: string;
}

export interface IRuleValidation extends IRuleValidationData {
  id: number;
}

export interface IUpdateRule {
  default?: string;
  editable?: boolean;
  required?: boolean;
  unique?: boolean;
  active?: boolean;
}

export interface ICreateRule extends IUpdateRule {
  target?: TargetType;
  field_name: string;
}

export interface IRuleWithValidation extends ICreateRule {
  validations: IRuleValidation[];
}

export interface IUpdateProfileField extends IUpdateRule {
  field?: string;
  title?: string;
  mapping_vcard?: string;
  allowed_as_login?: boolean;
  claim?: PrivateScopesMode;
}

export interface TCreateProfileField extends IUpdateRule {
  field: string;
  title: string;
  mapping_vcard?: string;
  allowed_as_login?: boolean;
  claim: PrivateScopesMode;
}

export interface IProfileField {
  type: 'general' | 'custom';
  field: string;
  title: string;
  default?: string;
  required: boolean;
  unique: boolean;
  active: boolean;
  editable: boolean;
  claim: EClaimPrivacy;
  mapping_vcard?: string;
  allowed_as_login?: boolean;
}

export interface ICreateClientType {
  name: string;
}

export interface IClientType extends ICreateClientType {
  id: string;
  name: string;
}

export interface IProviderRule {
  type: ProviderType;
  allowedScopes: ProviderScope[];
  unique: boolean;
  editable: boolean;
  requireable: boolean;
  deletable: boolean;
  title: string;
}

export enum ProviderScope {
  login = 'login',
  trusted = 'trusted',
  otp = 'otp',
  internal = 'internal',
}

export type TSettings = {
  registration_policy: string;
  allow_everyone_to_create_applications: boolean;
  authorize_only_admins: boolean;
  auto_merge_users: boolean;
  allowed_symbols: string;
  allowed_special_symbols: string;
  min_uppercase_count: number;
  number: number;
  spec_char: number;
  length_char_min: number;
  length_char_max: number;
  max_age: number;
  min_age: number;
  default_public_profile_claims_oauth: string;
  default_public_profile_claims_gravatar: string;
  allowed_login_fields: string;
  sentry: TSentry;
};

export interface IEmailTemplate {
  id: string;
  action: EEmailAction;
  title: string;
  content: string;
  subject: string;
}

export type TUpdateSettings = Partial<TSettings>;

export const settingsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getSettings: builder.query<TSettings, void>({
      query: () => endPoints.settings,
      providesTags: [ETags.Settings],
    }),

    editSettings: builder.mutation<IClientFull, TUpdateSettings>({
      query: (body) => createFetchArgsWithBody(endPoints.settings, 'PUT', body),
      invalidatesTags: [ETags.Settings, ETags.ClientDetails],
    }),

    getRules: builder.query<IRuleWithValidation[], void>({
      query: () => `${endPoints.settings}/rules`,
      providesTags: [ETags.Rules],
    }),

    getProfileFields: builder.query<IProfileField[], void>({
      query: () => `${endPoints.settings}/profile_fields`,
      providesTags: [ETags.ProfileFields],
    }),

    createProfileField: builder.mutation<void, TCreateProfileField>({
      query: (body) =>
        createFetchArgsWithBody(`${endPoints.settings}/profile_fields`, 'POST', body),
      invalidatesTags: [ETags.ProfileFields],
    }),

    updateProfileField: builder.mutation<void, { field_name: string; body: IUpdateProfileField }>({
      query: ({ field_name, body }) =>
        createFetchArgsWithBody(`${endPoints.settings}/profile_fields/${field_name}`, 'PUT', body),
      invalidatesTags: [ETags.ProfileFields],
    }),

    deleteProfileField: builder.mutation<void, string>({
      query: (field_name) =>
        createFetchArgs(`${endPoints.settings}/profile_fields/${field_name}`, 'DELETE'),
      invalidatesTags: [ETags.ProfileFields],
    }),

    getRuleValidations: builder.query<IRuleValidation[], void>({
      query: () => `${endPoints.settings}/rules_validations`,
      providesTags: [ETags.Rules],
    }),

    getRuleValidationsByFieldName: builder.query<IRuleValidation[], string>({
      query: (field_name) => `${endPoints.settings}/rules_validations/${field_name}`,
      providesTags: [ETags.Rules],
    }),

    getClientTypes: builder.query<IClientType[], void>({
      query: () => `${endPoints.settings}/client_types`,
      providesTags: [ETags.ClientTypes],
    }),

    createClientType: builder.mutation<void, ICreateClientType>({
      query: (body) => createFetchArgsWithBody(`${endPoints.settings}/client_types`, 'POST', body),
      invalidatesTags: [ETags.ClientTypes],
    }),

    updateClientType: builder.mutation<void, { id: string; body: ICreateClientType }>({
      query: ({ id, body }) =>
        createFetchArgsWithBody(`${endPoints.settings}/client_types/${id}`, 'PUT', body),
      invalidatesTags: [ETags.ClientTypes],
    }),

    getEmailTemplates: builder.query<IEmailTemplate[], void>({
      query: () => `${endPoints.settings}/email_templates`,
      providesTags: [ETags.EmailTemplates],
    }),

    updateEmailTemplates: builder.mutation<void, { action: string; body: Partial<IEmailTemplate> }>({
      query: ({ action, body }) =>
        createFetchArgsWithBody(`${endPoints.settings}/email_templates/${action}`, 'PUT', body),
      invalidatesTags: [ETags.EmailTemplates],
    }),

    deleteClientType: builder.mutation<void, string>({
      query: (id) => createFetchArgs(`${endPoints.settings}/client_types/${id}`, 'DELETE'),
      invalidatesTags: [ETags.ClientTypes],
    }),

    deleteRuleValidation: builder.mutation<void, number>({
      query: (id) => createFetchArgs(`${endPoints.settings}/rules_validations/${id}`, 'DELETE'),
      invalidatesTags: [ETags.Rules],
    }),

    createRuleValidation: builder.mutation<void, IRuleValidationData>({
      query: (body) =>
        createFetchArgsWithBody(`${endPoints.settings}/rules_validations`, 'POST', body),
      invalidatesTags: [ETags.Rules],
    }),

    updateRuleValidation: builder.mutation<void, { id: number; body: IRuleValidationData }>({
      query: ({ id, body }) =>
        createFetchArgsWithBody(`${endPoints.settings}/rules_validations/${id}`, 'PUT', body),
      invalidatesTags: [ETags.Rules],
    }),

    addRuleValidationToRule: builder.mutation<void, { field_name: string; id: number }>({
      query: ({ field_name, id }) =>
        createFetchArgs(
          `${endPoints.settings}/rules/${field_name}/rules_validations/${id}`,
          'POST',
        ),
      invalidatesTags: [ETags.Rules],
    }),

    removeRuleValidationFromRule: builder.mutation<void, { field_name: string; id: number }>({
      query: ({ field_name, id }) =>
        createFetchArgs(
          `${endPoints.settings}/rules/${field_name}/rules_validations/${id}`,
          'DELETE',
        ),
      invalidatesTags: [ETags.Rules],
    }),

    getTypes: builder.query<IClientType[], void>({
      query: () => `${endPoints.settings}/client_types`,
    }),

    getCatalogEnabled: builder.query<boolean, void>({
      query: () => `${endPoints.settings}/catalog`,
      providesTags: [ETags.Catalog],
    }),
    updateCatalogEnabled: builder.mutation<void, boolean>({
      query: (enabled) =>
        createFetchArgs(`${endPoints.settings}/catalog?enabled=${enabled}`, 'PUT'),
      invalidatesTags: [ETags.Catalog],
    }),
  }),
});

export const {
  useGetCatalogEnabledQuery,
  useUpdateCatalogEnabledMutation,
  useGetEmailTemplatesQuery,
  useUpdateEmailTemplatesMutation,
  useGetClientTypesQuery,
  useCreateClientTypeMutation,
  useDeleteClientTypeMutation,
  useUpdateClientTypeMutation,
  useGetSettingsQuery,
  useEditSettingsMutation,
  useGetRulesQuery,
  useGetProfileFieldsQuery,
  useCreateProfileFieldMutation,
  useUpdateProfileFieldMutation,
  useDeleteProfileFieldMutation,
  useGetRuleValidationsQuery,
  useGetRuleValidationsByFieldNameQuery,
  useDeleteRuleValidationMutation,
  useCreateRuleValidationMutation,
  useUpdateRuleValidationMutation,
  useAddRuleValidationToRuleMutation,
  useRemoveRuleValidationFromRuleMutation,
  useGetTypesQuery,
} = settingsApi;
